import { useState, useEffect, useCallback } from "react";
import { Bell, BellOff, Download, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { toast } from "sonner";

/**
 * Bandeau discret affiche en haut a droite pour :
 *  1. Proposer l'installation de la PWA (event beforeinstallprompt)
 *  2. Proposer l'activation des notifications push
 *
 * Le bandeau ne s'affiche qu'une fois que l'utilisateur est connecte ET que
 * l'une des 2 actions est disponible (a installer ou a activer).
 * Persistance localStorage : `pwa.dismissed` evite de re-proposer en boucle.
 */
export default function PWAPrompt() {
  const { user, axiosAuth } = useAuth();
  const [installEvent, setInstallEvent] = useState(null);
  const [pushPermission, setPushPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "denied");
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [dismissed, setDismissed] = useState(localStorage.getItem("pwa.dismissed") === "1");

  useEffect(() => {
    const onBip = (e) => { e.preventDefault(); setInstallEvent(e); };
    window.addEventListener("beforeinstallprompt", onBip);
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  // Verifier si une subscription push existe deja
  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      setPushSubscribed(!!sub);
    }).catch(() => {});
  }, []);

  const installPWA = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === "accepted") toast.success("Application installee");
    setInstallEvent(null);
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
  };

  const enablePush = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      toast.error("Notifications push non supportees par ce navigateur");
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      setPushPermission(perm);
      if (perm !== "granted") {
        toast.warning("Notifications refusees");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const api = axiosAuth();
      const { data: vapid } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.public_key),
      });
      const subJson = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        user_agent: navigator.userAgent,
      });
      setPushSubscribed(true);
      toast.success("Notifications push activees");
    } catch (e) {
      console.error(e);
      toast.error("Impossible d'activer les notifications");
    }
  }, [axiosAuth]);

  const dismiss = () => { localStorage.setItem("pwa.dismissed", "1"); setDismissed(true); };

  if (!user || dismissed) return null;
  const canInstall = !!installEvent;
  const canEnablePush = pushPermission !== "granted" && !pushSubscribed && pushPermission !== "denied";
  if (!canInstall && !canEnablePush) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-50 max-w-sm bg-[#14101c] border border-[#f7c935]/30 rounded-xl shadow-2xl shadow-[#f7c935]/10 p-4"
      data-testid="pwa-prompt"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-slate-500 hover:text-white"
        data-testid="pwa-dismiss"
        aria-label="Fermer"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#f7c935]/10 border border-[#f7c935]/30 flex items-center justify-center flex-shrink-0">
          {canInstall ? <Download className="w-5 h-5 text-[#f7c935]" /> : <Bell className="w-5 h-5 text-[#f7c935]" />}
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-white mb-1">
            {canInstall ? "Installer Blitzz" : "Activer les notifications"}
          </h4>
          <p className="text-xs text-slate-400 mb-3">
            {canInstall
              ? "Acces rapide depuis votre ecran d'accueil. Mode hors-ligne disponible."
              : "Recevez les approbations, evaluations et messages clients en temps reel."}
          </p>
          <div className="flex gap-2">
            {canInstall && (
              <Button onClick={installPWA} size="sm" data-testid="pwa-install-btn" className="bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold text-xs">
                <Download className="w-3.5 h-3.5 mr-1" /> Installer
              </Button>
            )}
            {canEnablePush && (
              <Button onClick={enablePush} size="sm" data-testid="pwa-push-btn" variant={canInstall ? "ghost" : "default"} className={canInstall ? "text-slate-300 text-xs" : "bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold text-xs"}>
                <Bell className="w-3.5 h-3.5 mr-1" /> Activer notifs
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
